// ── Submission planning & tracking types (Iteration 3, Module 5 Feature 2) ───
// Mirrors Part A of the Iteration 3 brief: the submission plan model (§1), the
// migration item entity (§2), the re-scan gate (§3), agility attributes (§4),
// plan versions (§5) and the field provenance every plan field carries. Table
// names follow I1 Engineering Note 2 as amended (I3 §13 g).

// ── Field provenance (§1.2, Note 2) ─────────────────────────────────────────
// Stored as a sibling JSON column (`field_meta`) keyed by field name rather
// than per-field columns; the chip renderer reads it generically.

export type FieldSource = 'discovery' | 'cbom' | 'playbook' | 'vendors' | 'migration_item' | 'readiness' | 'manual'

export interface FieldMeta {
  source: FieldSource
  source_ref: string | null
  synced_at: string | null
  overridden: boolean
  // The value the source produced at the last sync, kept so a later sync can
  // tell when an overridden field's source value has since changed (§11).
  source_value?: string | null
  // When overridden: the source the value came from before the manual edit.
  was?: FieldSource | null
  // Set by sync when an overridden field's source value has changed since the
  // override; cleared by Revert (§11 attention check).
  source_changed?: boolean
}

export type FieldMetaMap = Record<string, FieldMeta>

export const SOURCE_LABEL: Record<FieldSource, string> = {
  discovery: 'Discovery',
  cbom: 'CBOM',
  playbook: 'Playbook',
  vendors: 'Vendors',
  migration_item: 'Migration item',
  readiness: 'Readiness',
  manual: 'Manual',
}

// ── Submission plan (§1): Part A, one live row per org × index ──────────────

export type BudgetOption = 'allocated' | 'under_review' | 'plan_to_request' | 'not_committed' | 'other'
export type RiskFrameworkOption = 'documented' | 'under_review' | 'plan_next_cycle' | 'not_incorporated' | 'other'
export type ProcurementOption = 'incorporated' | 'under_review' | 'plan_next_review' | 'not_incorporated' | 'other'

export interface OptionSection<O extends string> {
  options: O[]
  other_text: string
  elaboration: string
}

export interface PlanContact {
  name: string
  title: string
  email: string
}

export interface Challenge {
  blocker: string
  support_needed: string
}

export interface MigrationPlan {
  id: string
  org_id: string
  index_id: string
  // Header
  org_name: string
  cii_sector: string
  accountable_exec_name: string
  accountable_exec_title: string
  contacts: PlanContact[]
  // Sections (CSA template Part A, in template order)
  s1_leadership: string
  s2_budget: OptionSection<BudgetOption>
  s3_crypto_management: string
  s4_risk_framework: OptionSection<RiskFrameworkOption>
  s5_procurement: OptionSection<ProcurementOption>
  s6_challenges: Challenge[]
  field_meta: FieldMetaMap
  // The live version v{n} and its state; served by the backend with the plan.
  plan_state: PlanVersionState
  // When the backend last refreshed the plan from Discovery.
  last_sync_at: string | null
  created_at: string
  updated_at: string
}

export type PartASectionKey = 's1_leadership' | 's2_budget' | 's3_crypto_management' | 's4_risk_framework' | 's5_procurement' | 's6_challenges'

// ── System annex (Part B, one per CII system) ───────────────────────────────

export type AnnexStatus = 'not_started' | 'in_progress' | 'complete'

export interface SystemAnnex {
  id: string
  plan_id: string
  system_id: string | null // Discovery system; null for a blank annex
  sort_order: number
  system_name: string
  description: string
  function_supported: string
  // Section 3, one answer as the template asks it (its five bullets are examples).
  s3_text: string
  // The five Section 3 fields versions saved before s3_text carry; read only
  // to render those versions.
  s3_continuity?: string
  s3_interoperability?: string
  s3_performance?: string
  s3_supply_chain?: string
  s3_capacity?: string
  status: AnnexStatus
  marked_complete_by: string | null
  marked_complete_at: string | null
  field_meta: FieldMetaMap
  // When Discovery data for this annex was last pulled (attention check: > 30 days).
  discovery_synced_at: string | null
  // Set by a sync when the system is no longer flagged CII in Discovery (or
  // vanished from it): the annex stays, out of completeness, checks and export.
  no_longer_cii: boolean
  // Set when a person archived the annex (e.g. the system is no longer treated
  // as CII): out of Part B, completeness, checks and export, and left alone by
  // syncs even while Discovery still flags the system CII. Restorable.
  archived_at: string | null
  archived_by: string | null
  archive_reason: string
  // The section-2 blocks, in order; served nested by the backend.
  blocks: AnnexAsset[]
  created_at: string
  updated_at: string
}

export type WillMigrate = 'Y' | 'N' | null

export type ProtocolCategory = 'algorithm' | 'protocol' | 'certificate' | 'other'

export interface ProtocolLine {
  name: string
  key_size: number | null
  protocol_version: string
  primitive: string
  category: ProtocolCategory
  count: number
}

export const PROTOCOL_CATEGORY_LABEL: Record<ProtocolCategory, string> = {
  algorithm: 'Algorithms',
  protocol: 'Protocols',
  certificate: 'Certificates',
  other: 'Keys & other material',
}

// Part B section 2 repeats per cryptographic asset within a system (§1.1).
// The asset is one Discovery source of the system (a repository, host, HSM,
// KMS or CA); its crypto findings roll up into protocols_algorithms.
export interface AnnexAsset {
  id: string
  annex_id: string
  source_id: string | null // Discovery source; null for a manual row
  // Set by a sync when the source left the system or Discovery; the block stays.
  source_removed_at: string | null
  sort_order: number
  asset_model_version: string
  inventorisation_type: string
  service_provided: string
  protocols_algorithms: string
  // The roll-up behind protocols_algorithms as rows, one per distinct line of
  // this asset's findings (counts are this asset's only). Empty for a manual
  // row; absent from versions saved before it existed.
  protocol_lines?: ProtocolLine[]
  package_type_vendor: string
  hosting: string
  will_migrate: WillMigrate
  migrate_solution_timeline: string
  dependencies: string
  vendor_qs_roadmap: string
  notes: string
  field_meta: FieldMetaMap
  created_at: string
  updated_at: string
}

// The nine CSA fields of a section-2 asset block, in template order (§10.2).
// `csa` is the template's row prompt, verbatim; `label` is the short form;
// `example` is the template's shaded example value, shown as the placeholder.
export const ASSET_FIELDS: Array<{ key: keyof AnnexAsset & string; label: string; csa: string; example: string; long?: boolean }> = [
  { key: 'asset_model_version', label: 'Asset, model and version', csa: 'Cryptographic asset, model/version', example: 'Microsoft AD CS (Windows Server 2019, v10.0)' },
  { key: 'inventorisation_type', label: 'Inventorisation type', csa: 'Type of inventorisation (e.g. manually, tooling, external services)', example: 'Semi-automated and supplemented by manual verification' },
  { key: 'service_provided', label: 'Service provided', csa: 'Service provided by the asset', example: 'Issues TLS certificates for internal services and code-signing certificates for firmware updates; depends on Root CA hosted on Luna HSM.' },
  { key: 'protocols_algorithms', label: 'Protocols and algorithms', csa: 'Protocols & algorithms in use', example: 'X.509 / RSA-2048 (key exchange); SHA-256 (hashing); ECDSA P-256 (select internal services)' },
  { key: 'package_type_vendor', label: 'Package type and vendor', csa: 'If part of a software package, indicate if Commercial-Off-the-Shelf (COTS), Government-provided, Other (e.g. custom software) and the name of the vendor/developer', example: 'COTS — Microsoft Corporation' },
  { key: 'hosting', label: 'Hosting', csa: 'Indicate if the asset is hosted by the agency (on-premise), on cloud (provide the name of the provider), hybrid', example: 'On-premise' },
  { key: 'will_migrate', label: 'Will this asset be migrated?', csa: 'Will this asset be migrated (Y/N)? Y – Indicate the choice of quantum-safe solutions, timeline. N – Provide justification (e.g. vendor readiness, legacy system limits) and mitigation controls', example: 'ML-KEM (FIPS 203) for key encapsulation; ML-DSA (FIPS 204) for code-signing and certificate issuance, pending Microsoft AD CS support. Cryptographic discovery complete: Q2 2025. Pilot migration: Q3 2026. Full migration target: Q4 2027, contingent on Microsoft AD CS PQC support release', long: true },
  { key: 'dependencies', label: 'Dependencies on this asset', csa: 'Dependencies on this asset', example: '47 internal services; firmware update pipeline for OT systems; VPN gateway authentication', long: true },
  { key: 'vendor_qs_roadmap', label: 'Vendor quantum-safe roadmap', csa: 'Vendor quantum-safe roadmap', example: 'Microsoft has indicated PQC support in AD CS is under development; no confirmed release date as of submission. Organisation is tracking vendor advisories and has raised PQC requirements in account review.', long: true },
]

// The template's "Any additional notes" row, kept beside the nine scored fields.
export const NOTES_FIELD = { key: 'notes', label: 'Any additional notes', csa: 'Any additional notes', example: 'Legacy OT systems dependent on AD CS-issued certificates may not support PQC algorithms — under separate review. Budget for migration tooling submitted under FY2026 request.' } as const

// Section 3 is one answer; the template's five bullets are what it should cover.
export const SECTION3_FIELD = {
  key: 's3_text',
  label: 'Operational continuity and potential risks',
  prompts: [
    ['Service continuity during transition', 'How the organisation will maintain availability and integrity of critical services during cryptographic replacement activities, including planned maintenance windows and rollback procedures.'],
    ['Interoperability during transition', 'How the organisation will manage interoperability between systems that have migrated to PQC and those still on classical cryptography, including dependencies on external parties.'],
    ['Performance impact', 'Whether any systems have latency, throughput, or resource constraints that may be affected by PQC algorithm adoption, and how these will be managed.'],
    ['Dependency and supply chain risks', 'How delays in vendor product readiness or third-party service provider timelines will be managed if they affect the migration schedule.'],
    ['Resource and capacity planning', 'How the organisation will ensure sufficient technical resource and capacity to execute migration without disrupting business-as-usual operations.'],
  ] as const,
} as const

// Section 3 of an annex as text: s3_text, or for a version saved before it,
// the five old fields as labelled paragraphs (as migration 022 joins them).
export function section3Text(a: SystemAnnex): string {
  if (a.s3_text?.trim()) return a.s3_text
  const legacy = [a.s3_continuity, a.s3_interoperability, a.s3_performance, a.s3_supply_chain, a.s3_capacity]
  return SECTION3_FIELD.prompts.map(([label], i) => (legacy[i]?.trim() ? `${label}: ${legacy[i]!.trim()}` : '')).filter(Boolean).join('\n\n')
}

// ── Migration items (§2) ────────────────────────────────────────────────────

export type MigrationItemStatus = 'not_started' | 'planned' | 'in_progress' | 'verified' | 'closed'
export const ITEM_STATUSES: MigrationItemStatus[] = ['not_started', 'planned', 'in_progress', 'verified', 'closed']
export const ITEM_STATUS_LABEL: Record<MigrationItemStatus, string> = {
  not_started: 'Not started',
  planned: 'Planned',
  in_progress: 'In progress',
  verified: 'Verified',
  closed: 'Closed',
}

// Iteration 3 §12: a recommended step a migration item delivers.
export interface StepLink {
  objective_code: string
  transition: string // e.g. 'L1>L2'
  bullet_index: number
}

export interface MigrationItem {
  id: string
  org_id: string
  code: string // MI-nnn
  title: string
  system_id: string | null
  // Iteration 3 §8: an item belongs to exactly one Discovery component.
  component_id: string | null
  // The cryptographic capability the item changes (gating, §12 detection rule).
  capability: import('@/api/types/catalogue').Capability | null
  // Whether the target date follows the catalogue roadmap date (§8.2).
  target_source: 'catalogue' | 'owner'
  step_links: StepLink[]
  current_algorithm: string // from CBOM
  target_algorithm: string
  target_rationale_ref: string | null // Evaluation Template (FR-2.2), nullable hook
  owner_user_id: string | null
  target_date: string | null
  wave_id: string | null // readiness.migration_wave; no upstream serves a plan
  status: MigrationItemStatus
  created_from_finding_id: string | null
  created_from_group: boolean // created from a CBOM asset group (M3 F01)
  vendor_gated_year: number | null // target is a vendor release
  notes: string
  created_at: string
  updated_at: string
}

export interface MigrationItemAsset {
  item_id: string
  asset_id: string
}

export interface MigrationItemDependency {
  item_id: string
  depends_on_item_id: string
}

export type MigrationItemEventType = 'created' | 'status_changed' | 'scope_changed' | 'target_changed' | 'rescan_requested' | 'rescan_result' | 'closed' | 'wave_changed' | 'due_set'

export interface MigrationItemEvent {
  id: string
  item_id: string
  event_type: MigrationItemEventType
  actor: string
  occurred_at: string
  from: string | null
  to: string | null
  note: string | null
}

// ── Re-scan close-out gate (§3) ─────────────────────────────────────────────

export interface RescanOutcome {
  asset_id: string
  vulnerable_present: boolean
  scan_id: string
  scanned_at: string
}

export interface RescanResult {
  id: string
  item_id: string
  scan_id: string
  scanner: string // platform setting; Discovery M1 F12 by default
  requested_by: string
  requested_at: string
  completed_at: string
  outcomes: RescanOutcome[]
}

export interface CloseGate {
  ok: boolean
  failing_asset_ids: string[]
  scan_ref: RescanResult | null
  reason: 'no_scan' | 'stale_scan' | 'vulnerable' | 'no_assets' | null
}

// ── Cryptographic agility attributes (§4) ───────────────────────────────────

export interface SystemAgility {
  system_id: string
  algorithm_configurable: boolean
  key_rotation_automated: boolean
  library_abstracted: boolean
  note: string
  set_by: string | null
  set_at: string | null
}

// ── Plan versions (§5) ──────────────────────────────────────────────────────

export type ExportFormat = 'docx' | 'pdf'

export interface PlanSnapshot {
  plan: MigrationPlan
  annexes: SystemAnnex[]
  assets: AnnexAsset[]
  items: MigrationItem[]
  agility: SystemAgility[]
}

export interface MigrationPlanVersion {
  id: string
  plan_id: string
  // The draft v{n} this copy was taken from: saving and exporting never move n.
  plan_version: number
  label: string
  submission_date: string
  note: string | null
  snapshot: PlanSnapshot
  completeness: number // 0–100
  blocking_count: number
  attention_items: PlanCheck[]
  created_by: string
  created_at: string
  exported_formats: ExportFormat[]
  export_log: string[]
}

// ── Submission states (Iteration 3 §6, §7) ─────────────────────────────────
// The live plan is version v{n}. A version is created as draft; Mark as
// submitted freezes it; Start new version creates the next draft from it.

export type PlanVersionStatus = 'draft' | 'submitted'

export interface PlanVersionState {
  n: number
  status: PlanVersionStatus
  started_at: string
  started_by: string
  submission_date: string | null // planned date to submit to CSA
  submission_date_confirmed: boolean
}

// submission_marked (§7.1): written verbatim, shown in the submission record.
export interface SubmissionMarked {
  id: string
  version: number
  user_id: string
  submitted_at: string
  time_zone: string
  recorded_at: string
  ip: string | null // recorded server-side
  csa_reference: string | null
  acknowledgement_id: string | null
  acknowledgement_name: string | null
  open_checks: string[]
  note: string | null
  reauth: 'password' | 'sso'
  final_pdf_version_id: string | null // the stored final PDF (export snapshot)
}

// submission_reopened: administrator only, with a reason (§7.1).
export interface SubmissionReopened {
  id: string
  version: number
  user_id: string
  reason: string
  at: string
}

// ── Checks (§11) ────────────────────────────────────────────────────────────

export type BlockingKind = 'cii_no_annex' | 'asset_y_no_solution' | 'parta_section_empty' | 'annex_zero_assets'
export type AttentionKind = 'vendor_roadmap_missing' | 'annex_stale_discovery' | 'item_verified_not_closed' | 'overridden_source_changed'

export interface PlanCheck {
  code: string
  kind: BlockingKind | AttentionKind
  blocking: boolean
  text: string
  fix: { annex_id?: string; asset_id?: string; item_id?: string; part?: 'a'; section?: PartASectionKey; system_id?: string }
}

export interface PlanChecks {
  blocking: PlanCheck[]
  attention: PlanCheck[]
  // Plan completeness, 0–100, as the backend scores it.
  completeness: number
}

// ── Plan-level events (analytics, I2 Note 10 as amended) ────────────────────

export type MigrationAnalyticsEvent =
  | { name: 'migration.item_created'; item_id: string; from: 'finding' | 'group' | 'blank' }
  | { name: 'migration.item_status_changed'; item_id: string; from: MigrationItemStatus; to: MigrationItemStatus }
  | { name: 'migration.rescan_requested'; item_id: string }
  | { name: 'migration.item_closed'; item_id: string }
  | { name: 'plan.annex_added'; annex_id: string; from: 'discovery' | 'blank' | 'duplicate' }
  | { name: 'plan.annex_removed'; annex_id: string }
  | { name: 'plan.version_saved'; version_id: string }
  | { name: 'plan.exported'; version_id: string; formats: ExportFormat[] }

export type PlanEventType = 'submission_marked' | 'submission_reopened' | 'version_started' | 'plan_updated' | 'annex_added' | 'annex_removed' | 'annex_archived' | 'annex_restored' | 'annex_reordered' | 'annex_marked_complete' | 'asset_added' | 'asset_removed' | 'field_overridden' | 'field_reverted' | 'synced' | 'plan_version_saved' | 'plan_exported'

export interface PlanEvent {
  id: string
  plan_id: string
  event_type: PlanEventType
  actor: string
  occurred_at: string
  ref: string | null
  note: string | null
}

// ── Facts the module reads from the platform (§12) ──────────────────────────

export interface DiscoverySystem {
  id: string
  name: string
  description: string
  function_supported: string
  hosting: string
  inventorisation_type: string
  cii: boolean
  inventory_id: string | null
  business_unit: string
  asset_count: number
  last_scan_at: string | null
}

// Iteration 3 §8: a component is a feature-level part of a system, detected by
// Discovery; assets are only useful as part of a component.
export interface DiscoveryComponent {
  id: string
  system_id: string
  name: string
  description: string
}

export interface CbomAsset {
  id: string
  code: string // short label, e.g. A-0142
  system_id: string
  component_id: string | null
  // Product name Discovery reports for the asset (catalogue matching, §9.3).
  detected_technology?: string | null
  name: string
  model_version: string
  asset_type: string
  service_provided: string
  protocols_algorithms: string
  package_type_vendor: string
  vendor_id: string | null
  current_algorithm: string
  quantum_vulnerable: boolean
  // What the configured scanner would report today: whether the vulnerable
  // algorithm is still present on the asset.
  vulnerable_present: boolean
  group_id: string | null // CBOM asset group (M3 F01)
  group_name: string | null
}

export interface DiscoveryFinding {
  id: string
  system_id: string
  asset_ids: string[]
  algorithm: string
  title: string
  severity: 'high' | 'medium' | 'low'
  found_at: string
}

export interface WaveDef {
  id: string
  label: string
  order: number
  from: string
  to: string
  rationale: string
}

export interface VendorRoadmap {
  vendor_id: string
  name: string
  roadmap: string | null
  risk_flags: string[]
}

export interface PlaybookNote {
  system_id: string
  kind: 'continuity' | 'dependency' | 'supply_chain'
  text: string
  ref: string
}

export interface MigrationFacts {
  connected: boolean
  scanner: string
  systems: DiscoverySystem[]
  components: DiscoveryComponent[]
  assets: CbomAsset[]
  findings: DiscoveryFinding[]
  waves: WaveDef[]
  wave_plan_updated_at: string | null
  vendors: VendorRoadmap[]
  playbook_notes: PlaybookNote[]
  fetched_at: string
}
